// Per-questionnaire vendor signals derived from answers alone. Kept React-free
// and unit-tested (this repo has no DOM test environment).
//
// Also the single source of truth for "what does the VENDOR still need to act
// on" (ported from the sister repo's packages/ui/src/lib/vendorActions.ts): the
// portal nav badge, the picker's "Action required · n" pill, the wizard's
// "Needs action" filter and the per-question red border all count through
// needsVendorAction, so they can never disagree.
//
// The rule itself now lives in @l2u/shared/vendorActions — re-exported below
// under the same names — so the API can compute the nav badge with it too
// (GET /api/me/nav-counts, GET /api/driver/me). What stays here is the
// presentation copy.
import {
  isReturnedReason,
  vendorActionCountFromWire,
  type ActionReason,
  type VendorActionItem,
} from '@l2u/shared/vendorActions';
import type { Question, Submission } from './types.ts';

export {
  isActionable,
  isReturnedReason,
  needsVendorAction,
  questionnaireActionItems,
  questionnaireSubmitted,
  type ActionProgress,
  type ActionReason,
  type VendorActionItem,
} from '@l2u/shared/vendorActions';

/**
 * The nav-badge total — vendorActionCount in @l2u/shared/vendorActions has the
 * rule: one per unsubmitted questionnaire, plus each submitted one's returned
 * answers.
 *
 * Read off the wire shapes through vendorActionCountFromWire, the very call the
 * API makes for GET /api/me/nav-counts and GET /api/driver/me, so the two
 * cannot disagree. The portal calls this only against an API that predates the
 * server count — see apps/portal/src/lib/useVendorActionCount.ts.
 */
export function vendorActionBadgeCount(questions: Question[], submissions: Submission[]): number {
  return vendorActionCountFromWire(questions, submissions);
}

/** One-line reason summary for a set of action items. */
export function actionReasonSummary(items: VendorActionItem[]): string {
  const hasComment = items.some((i) => isReturnedReason(i.reason));
  const hasUnanswered = items.some((i) => i.reason === 'unanswered');
  const hasDocument = items.some((i) => i.reason === 'document_required');
  if (hasComment && (hasUnanswered || hasDocument)) return 'Reply to comments & answer questions';
  if (hasComment) return 'Reply to admin comments';
  // Only documents outstanding — say so rather than asking for answers the
  // vendor has already given.
  if (hasDocument && !hasUnanswered) return 'Document required';
  return 'Answer outstanding questions';
}

/** The short per-question label shown beside "Action required" on a card. */
export function actionReasonLabel(reason: ActionReason): string {
  switch (reason) {
    case 'rejected':
      return 'Changes requested';
    case 'admin_comment':
      return 'Reply to the admin';
    case 'document_required':
      return 'Document required';
    case 'unanswered':
      return 'Needs an answer';
  }
}

/**
 * Client mirror of the server's `questionnaireSubmittedAt` (apps/api/src/lib/
 * questionnaireLock.ts): the submit timestamp of the questionnaire containing
 * these questions, or null if none of them has been submitted yet. Feeds
 * `isAnswerLocked` directly, which only tests this for truthiness — so, like
 * the server (an un-ordered `.limit(1)`, no ORDER BY), this returns whichever
 * stamped sibling it finds first rather than sorting for the earliest. A
 * `string | null` return (vs. a plain boolean) was chosen to keep the same
 * shape as every other `submitted_at` field in this codebase (Company, Driver,
 * Asset) and to avoid touching `isAnswerLocked` / `QuestionCard`'s existing
 * `string | null` prop.
 *
 * `questions` must already be scoped to the one questionnaire on screen —
 * this function does no scoping itself. Passing the full unscoped question
 * list here is the exact regression this helper exists to prevent: a stamp
 * on a DIFFERENT questionnaire's answer must never lock this one.
 */
export function scopedSubmittedAt(
  questions: { id: string }[],
  subByQ: ReadonlyMap<string, { submitted_at?: string | null }>,
): string | null {
  for (const q of questions) {
    const at = subByQ.get(q.id)?.submitted_at;
    if (at != null) return at;
  }
  return null;
}
